import React, { useState, useEffect } from "react";
import {
  AiOutlineCloudDownload,
  AiOutlineUpload,
  AiOutlineCheckCircle,
  AiOutlineFileDone,
  AiOutlineDashboard,
  AiOutlineUser,
  AiOutlineMail,
  AiOutlinePhone
} from "react-icons/ai";
import userService from "../services/userService";

export default function Dashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await userService.getUserDashboard();
        if (response.success) {
          setDashboardData(response.data);
        } else {
          setError("Failed to load dashboard data");
        }
      } catch (err) {
        setError("Failed to load dashboard data");
        console.error("Error fetching dashboard data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger text-center" role="alert">
        {error}
      </div>
    );
  }

  const stats = [
    {
      icon: <AiOutlineCloudDownload size={22} color="#4338ca" />,
      color: "#4338ca",
      bg: "#ede9fe",
      label: "Total Downloads",
      value: dashboardData?.stats?.total_downloads ?? 0,
    },
    {
      icon: <AiOutlineUpload size={22} color="#059669" />,
      color: "#059669",
      bg: "#d1fae5",
      label: "Total Uploads",
      value: dashboardData?.stats?.total_uploads ?? 0,
    },
    {
      icon: <AiOutlineCheckCircle size={22} color="#2563eb" />,
      color: "#2563eb",
      bg: "#dbeafe",
      label: "Total Drafts Received",
      value: dashboardData?.stats?.tax_returns ?? 0,
    },
    {
      icon: <AiOutlineFileDone size={22} color="#ea580c" />,
      color: "#ea580c",
      bg: "#fff7ed",
      label: "Final Copies Received",
      value: dashboardData?.stats?.pending_requests ?? 0,
    },
  ];

  return (
    <section>
      <h1
        style={{
          fontSize: 38,
          marginRight: 10,
          color: "#4338ca",
          verticalAlign: "middle",
          filter: "drop-shadow(0px 1px 4px #b5beff77)",
        }}
        tabIndex={-1}
      >
       <AiOutlineDashboard/> Dashboard
      </h1>

      <div
        style={{
          display: "flex",
          gap: 18,
          flexWrap: "wrap",
          justifyContent: "flex-start",
        }}
      >
        {stats.map((stat, i) => (
          <StatCard key={i} {...stat} />
        ))}
      </div>

      {/* Point of Contact Section */}
      <div
        style={{
          marginTop: 32,
          padding: 24,
          borderRadius: 16,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          boxShadow: "0 8px 24px rgba(102, 126, 234, 0.25)",
          color: "#fff",
        }}
      >
        <h2
          style={{
            fontSize: 22,
            fontWeight: 700,
            marginBottom: 20,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <AiOutlineUser size={24} />
          Point of Contact
        </h2>
        
        {dashboardData?.user?.assigned_employee_name ? (
          <div
            style={{
              background: "rgba(255, 255, 255, 0.15)",
              backdropFilter: "blur(10px)",
              borderRadius: 12,
              padding: 20,
              display: "flex",
              flexDirection: "column",
              gap: 16,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                fontSize: 18,
                fontWeight: 600,
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  background: "rgba(255, 255, 255, 0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 20,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {dashboardData.user.assigned_employee_name.charAt(0).toUpperCase()}
              </div>
              <span>{dashboardData.user.assigned_employee_name}</span>
            </div>
            
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {dashboardData.user.assigned_employee_email && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    fontSize: 15,
                    opacity: 0.95,
                  }}
                >
                  <AiOutlineMail size={18} style={{ flexShrink: 0 }} />
                  <a
                    href={`mailto:${dashboardData.user.assigned_employee_email}`}
                    style={{
                      color: "#fff",
                      textDecoration: "none",
                      transition: "opacity 0.2s",
                    }}
                    onMouseEnter={(e) => (e.target.style.opacity = "0.8")}
                    onMouseLeave={(e) => (e.target.style.opacity = "1")}
                  >
                    {dashboardData.user.assigned_employee_email}
                  </a>
                </div>
              )}
              
              {dashboardData.user.assigned_employee_mobile && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    fontSize: 15,
                    opacity: 0.95,
                  }}
                >
                  <AiOutlinePhone size={18} style={{ flexShrink: 0 }} />
                  <a
                    href={`tel:${dashboardData.user.assigned_employee_mobile}`}
                    style={{
                      color: "#fff",
                      textDecoration: "none",
                      transition: "opacity 0.2s",
                    }}
                    onMouseEnter={(e) => (e.target.style.opacity = "0.8")}
                    onMouseLeave={(e) => (e.target.style.opacity = "1")}
                  >
                    {dashboardData.user.assigned_employee_mobile}
                  </a>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div
            style={{
              background: "rgba(255, 255, 255, 0.15)",
              backdropFilter: "blur(10px)",
              borderRadius: 12,
              padding: 24,
              textAlign: "center",
              fontSize: 16,
              opacity: 0.9,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                marginBottom: 8,
              }}
            >
              <AiOutlineUser size={20} />
              <span style={{ fontWeight: 600 }}>No one is assigned yet</span>
            </div>
            <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
              Your assigned employee will appear here once an admin assigns one to you.
            </p>
          </div>
        )}
      </div>

      <div
        style={{
          marginTop: 32,
          paddingTop: 18,
          minHeight: 180,
          borderRadius: 16,
          background: "#f3f4f6",
          border: "2px dashed #c7d2fe",
          color: "#9ca3af",
          fontWeight: 600,
          fontSize: 18,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          userSelect: "none",
        }}
        aria-label="Widget placeholder"
      >
        Placeholder for graphs/arbitrary widgets
      </div>
    </section>
  );
}

function StatCard({ icon, color, bg, label, value }) {
  return (
    <article
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "16px 22px",
        width: 190,
        background: bg,
        borderRadius: 16,
        boxShadow: "0 6px 14px rgb(62 63 104 / 8%)",
        userSelect: "none",
      }}
    >
      <div style={{ flexShrink: 0 }}>{icon}</div>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <span
          style={{ color: color, fontWeight: 700, fontSize: 23, lineHeight: 1 }}
        >
          {value}
        </span>
        <span style={{ color: "#4b4b57", fontWeight: 500, fontSize: 13 }}>
          {label}
        </span>
      </div>
    </article>
  );
}
